import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Transition } from "@headlessui/react";
import {
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { PMPagination } from "../../../utils/PMPagination";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { dateToString, InitialsTag, StringLimit } from "../../../Utils";
import {
  DeviationPriority,
  DeviationSeverity,
  DeviationStatus,
} from "./utils";

export const DEVIATION_LIST_FIELDS =
  "id,number,displayTitle,title,created_at,updated_at,status,project,customer,contact,deviation_type,responsible_user,assigned_user,work_order,notification_statuses";

const notificationLabels = {
  customer: "Kunde",
  contact: "Kontakt",
  project_leader: "Prosjektleder",
  internal_project_leader: "Intern PL",
  custom: "Valgfri",
  sender_copy: "Avsender",
};

const getNotificationLabel = (key, value) => {
  if (notificationLabels[key]) return notificationLabels[key];
  if (key?.startsWith("project_contact_")) {
    return value?.recipient_name || "Prosjektkontakt";
  }
  return key;
};

const getNotificationDisplay = (key, value) => {
  if (value?.recipient_name) {
    const label = getNotificationLabel(key, value);

    if (label === value.recipient_name) {
      return label;
    }

    return `${label}: ${value.recipient_name}`;
  }

  return getNotificationLabel(key, value);
};

/** Single-line cell: first recipient/label, then " (+ n)" for the rest (keeps table row height stable). */
const NotifiedCompactCell = ({ statuses = {}, t }) => {
  const keys = Object.keys(statuses || {});
  if (keys.length === 0) {
    return (
      <span className="text-xs text-gray-500">
        {t("deviations.list.notified_none")}
      </span>
    );
  }
  const labels = keys.map((key) => getNotificationDisplay(key, statuses[key]));
  const first = labels[0];
  const rest = labels.length - 1;
  return (
    <span
      className="block w-full min-w-0 truncate text-sm text-gray-700"
      title={labels.join(", ")}
    >
      {first}
      {rest > 0
        ? t("deviations.list.notified_compact_suffix", { count: rest })
        : ""}
    </span>
  );
};

const NotificationChips = ({ statuses = {}, compact = false, t }) => {
  const keys = Object.keys(statuses || {});

  if (keys.length === 0) {
    return (
      <span className={compact ? "text-xs text-gray-500" : "text-sm text-gray-500"}>
        {t ? t("deviations.list.notified_none") : "Ingen varsler sendt"}
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {keys.map((key) => (
        <span
          key={key}
          className={`inline-flex items-center rounded-full bg-emerald-50 font-medium text-emerald-700 ${
            compact ? "px-2 py-1 text-[11px]" : "px-2.5 py-1 text-xs"
          }`}
        >
          {getNotificationDisplay(key, statuses?.[key])}
        </span>
      ))}
    </div>
  );
};

export const DeviationsTableNewView = ({
  itemState,
  onPageChange,
  itemsCount,
  totalCount,
  currentCount,
  page,
  perPage,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data = [], isLoading } = itemState;
  const [selectedDeviation, setSelectedDeviation] = useState(null);
  const [slidePanelOpen, setSlidePanelOpen] = useState(false);

  const handleRowClick = (deviation) => {
    setSelectedDeviation(deviation);
    setSlidePanelOpen(true);
  };

  const handleClosePanel = () => {
    setSlidePanelOpen(false);
    setSelectedDeviation(null);
  };

  return (
    <div className="flow-root min-w-0">
      <PMPagination
        disabled={isLoading}
        onPageChange={onPageChange}
        page={page}
        perPage={perPage}
        rowsCount={itemsCount}
        totalCount={totalCount}
        currentCount={currentCount}
        showCount={true}
        showPages={true}
        showNextPrev={true}
      />

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="p-8">
            <ResourceLoading />
          </div>
        ) : data.length === 0 ? (
          <div className="p-6">
            <ResourceEmpty />
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="w-full max-w-full table-fixed border-collapse divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-14 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:w-16 xl:px-3">
                    Nr.
                  </th>
                  <th className="w-[26%] min-w-0 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:px-3">
                    Avvik
                  </th>
                  <th className="w-[15%] min-w-0 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:px-3">
                    Kunde
                  </th>
                  <th className="w-[15%] min-w-0 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:px-3">
                    Arbeidsordre
                  </th>
                  <th className="w-[7rem] shrink-0 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:px-3">
                    Opprettet
                  </th>
                  <th className="w-[11%] min-w-0 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:px-3">
                    Status
                  </th>
                  <th className="w-[14%] min-w-0 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:px-3">
                    Ansvarlig
                  </th>
                  <th className="w-[13%] min-w-0 px-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:px-3">
                    Varslet
                  </th>
                  <th className="relative w-9 px-1 py-2.5 xl:w-10 xl:px-2">
                    <span className="sr-only">Handlinger</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {data.map((deviation) => (
                  <tr
                    key={deviation.id}
                    onClick={() => handleRowClick(deviation)}
                    className="group cursor-pointer transition-colors hover:bg-gray-50"
                  >
                    <td className="truncate px-2 py-2 text-sm font-medium text-blue-600 xl:px-3">
                      {deviation.number || "–"}
                    </td>
                    <td className="min-w-0 px-2 py-2 xl:px-3">
                      <div className="min-w-0 space-y-1">
                        <div
                          className="truncate text-sm font-medium text-gray-900"
                          title={
                            deviation.title ||
                            deviation.displayTitle ||
                            "Uten tittel"
                          }
                        >
                          <StringLimit
                            text={
                              deviation.title ||
                              deviation.displayTitle ||
                              "Uten tittel"
                            }
                            limit={48}
                          />
                        </div>
                        <div
                          className="truncate text-xs text-gray-500 sm:text-sm"
                          title={
                            deviation.deviation_type?.displayTitle ||
                            deviation.deviation_type?.title ||
                            ""
                          }
                        >
                          {deviation.deviation_type?.displayTitle ||
                            deviation.deviation_type?.title ||
                            "Type mangler"}
                        </div>
                        <div
                          className="truncate text-xs text-gray-400"
                          title={
                            deviation.project?.displayTitle || ""
                          }
                        >
                          {deviation.project?.displayTitle || "Prosjekt mangler"}
                        </div>
                      </div>
                    </td>
                    <td className="min-w-0 px-2 py-2 xl:px-3">
                      <p
                        className="truncate text-sm text-gray-600"
                        title={deviation.customer?.displayTitle || undefined}
                      >
                        {deviation.customer?.displayTitle || "–"}
                      </p>
                    </td>
                    <td className="min-w-0 px-2 py-2 xl:px-3">
                      <p
                        className="truncate text-sm text-gray-600"
                        title={deviation.work_order?.displayTitle || undefined}
                      >
                        {deviation.work_order?.displayTitle || "Ikke knyttet"}
                      </p>
                    </td>
                    <td className="truncate px-2 py-2 text-sm text-gray-600 xl:px-3">
                      {dateToString(deviation.created_at)}
                    </td>
                    <td className="min-w-0 px-2 py-2 xl:px-3">
                      <div className="min-w-0 space-y-1.5">
                        <DeviationStatus deviation={deviation} />
                        <div className="flex min-w-0 flex-wrap gap-1.5">
                          <DeviationSeverity severity={deviation?.severity} />
                          <DeviationPriority priority={deviation?.priority} />
                        </div>
                      </div>
                    </td>
                    <td className="min-w-0 px-2 py-2 xl:px-3">
                      {deviation.responsible_user?.displayTitle ? (
                        <div className="flex min-w-0 items-center gap-2">
                          <InitialsTag
                            name={deviation.responsible_user.displayTitle}
                          />
                          <span
                            className="min-w-0 truncate text-sm text-gray-600"
                            title={deviation.responsible_user.displayTitle}
                          >
                            {deviation.responsible_user.displayTitle}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-600">–</span>
                      )}
                    </td>
                    <td className="min-w-0 px-2 py-2 xl:px-3">
                      <NotifiedCompactCell
                        statuses={deviation?.notification_statuses}
                        t={t}
                      />
                    </td>
                    <td className="px-1 py-2 text-right xl:px-2">
                      <ChevronRightIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-600" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PMPagination
        disabled={isLoading}
        onPageChange={onPageChange}
        page={page}
        perPage={perPage}
        rowsCount={itemsCount}
        totalCount={totalCount}
        currentCount={currentCount}
        showCount={true}
      />

      {slidePanelOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20"
          onClick={handleClosePanel}
          onKeyDown={(event) => event.key === "Escape" && handleClosePanel()}
          role="button"
          tabIndex={0}
          aria-label="Close panel"
        />
      )}

      <Transition
        show={slidePanelOpen}
        enter="transition-transform duration-300 ease-out"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition-transform duration-200 ease-in"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed top-16 bottom-0 right-0 z-40 w-full max-w-md bg-white shadow-xl pt-4">
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedDeviation?.displayTitle || "Avvik"}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {selectedDeviation?.deviation_type?.displayTitle ||
                    selectedDeviation?.deviation_type?.title ||
                    "Type mangler"}
                </p>
              </div>
              <button
                type="button"
                onClick={handleClosePanel}
                className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 space-y-5 overflow-y-auto p-4">
              <div>
                <h4 className="text-xs font-medium uppercase text-gray-500">
                  Status
                </h4>
                <div className="mt-2 flex flex-wrap gap-2">
                  <DeviationStatus deviation={selectedDeviation} />
                  <DeviationSeverity severity={selectedDeviation?.severity} />
                  <DeviationPriority priority={selectedDeviation?.priority} />
                </div>
              </div>

              <div>
                <h4 className="text-xs font-medium uppercase text-gray-500">
                  Kunde
                </h4>
                <p className="mt-1 text-sm text-gray-700">
                  {selectedDeviation?.customer?.displayTitle || "–"}
                </p>
              </div>

              <div>
                <h4 className="text-xs font-medium uppercase text-gray-500">
                  Ansvarlig
                </h4>
                <p className="mt-1 text-sm text-gray-700">
                  {selectedDeviation?.responsible_user?.displayTitle || "–"}
                </p>
              </div>

              <div>
                <h4 className="text-xs font-medium uppercase text-gray-500">
                  Arbeidsordre
                </h4>
                <p className="mt-1 text-sm text-gray-700">
                  {selectedDeviation?.work_order?.displayTitle || "Ikke knyttet"}
                </p>
              </div>

              <div>
                <h4 className="text-xs font-medium uppercase text-gray-500">
                  Varslet
                </h4>
                <div className="mt-2">
                  <NotificationChips
                    statuses={selectedDeviation?.notification_statuses}
                    t={t}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 p-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/dashboard/hse-ks/deviations/${selectedDeviation?.id}`
                    )
                  }
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
                >
                  <DocumentTextIcon className="h-5 w-5" />
                  Åpne avvik
                </button>
                {selectedDeviation?.work_order?.id ? (
                  <Link
                    to={`/dashboard/work_orders/${selectedDeviation.work_order.id}`}
                    className="flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <WrenchScrewdriverIcon className="h-5 w-5" />
                  </Link>
                ) : (
                  <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-400">
                    <ClipboardDocumentListIcon className="h-5 w-5" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  );
};

DeviationsTableNewView.defaultProps = {
  page: 1,
  perPage: 25,
  itemState: { data: [] },
  itemsCount: 0,
  totalCount: 0,
  currentCount: 0,
};
